import React, { useState, useEffect } from "react";
import {
  useGetUserByIdQuery,
  useRegisterUserMutation,
} from "@/services/UserApi";
import { RegistrationFormData, } from "@/app/types/user";
import { Gender } from "@/app/shared_enums/enums";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/app/store";
import { useForm, Controller } from "react-hook-form";
import { toast } from "@/components/ui/UseToast";
import { Button } from "@/components/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import PhoneInputField from "@/components/custom-component/PhoneInputField";
import { updateUserInfo } from "@/features/auth/AuthSlice";
import "@/components/admin/authentication/style.css";

const UserForm: React.FC = () => {
  const userId = useSelector(
    (state: RootState) => state.auth.userInfo?.id ?? 0
  );
  const [isEditing, setIsEditing] = useState(false);
  const userInfo = JSON.parse(localStorage.getItem("userInfo") ?? "{}");

  const dispatch = useDispatch();

  const {
    data: user,
    isLoading,
    isError,
  } = useGetUserByIdQuery(Number(userId), {
    skip: !userId, // Skip query if userId is empty
  });

  const [registerUser, { isLoading: isUpdating }] = useRegisterUserMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<RegistrationFormData>({
    mode: "onBlur",
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
      email: "",
      phone_num: "",
      gender: Gender.MALE,
    },
  });

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = form;

  useEffect(() => {
    if (user) {
      // Populate form with user data when available
      reset({
        username: user.username || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        phone_num: user.phone_num || "",
        gender: user.gender || "",
      });
    }
  }, [user, reset]);

  const validatePhone = (value: string | undefined | null) => {
    if (!value) return "Phone number is required.";
    if (!/^\d+$/.test(value) || value.length < 10 || value.length > 15) {
      return "Enter a valid phone number.";
    }
    return true;
  };

const onSubmit = async (values: RegistrationFormData) => {
  setIsSubmitting(true);

  try {
    // Call API to update user data
    const res = await registerUser(values).unwrap();

    // Prepare updated user information
    const updatedUserInfo = {
      ...userInfo, // Retain existing user information
      first_name: res.first_name, // Update the first name from the response
      last_name: res.last_name, // Update the last name from the response
      username: res.username, // Update the username from the response
    };

    // Dispatch updated user information to Redux
    dispatch(updateUserInfo(updatedUserInfo));

    // Update localStorage with the latest user info
    localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));

    // Show success notification
    toast({
      variant: "success",
      description: "User information updated successfully.",
    });

    // Disable edit mode
    setIsEditing(false);
  } catch (error) {
    // Handle errors
    toast({
      variant: "destructive",
      description: `Failed to update user. ${(error as any)?.data?.message || "An error occurred."}`,
    });
  } finally {
    setIsSubmitting(false);
  }
};

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fix the highlighted errors.",
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md overflow-hidden rounded-lg border shadow-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Update User</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-gray-500">Loading user data...</p>
          ) : isError ? (
            <p className="text-center text-red-500">
              Failed to fetch user data.
            </p>
          ) : (
            <Form {...form}>
              <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="space-y-6"
              >
                <FormField
                  control={control}
                  name="username"
                  rules={{ required: "Username is required." }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input
                          disabled={!isEditing}
                          placeholder="Enter a unique username"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={control}
                    name="first_name"
                    rules={{ required: "First name is required." }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input
                            disabled={!isEditing}
                            placeholder="First Name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="last_name"
                    rules={{ required: "Last name is required." }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input
                            disabled={!isEditing}
                            placeholder="Last Name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={control}
                  name="email"
                  rules={{
                    required: "Email is required.",
                    pattern: {
                      value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                      message: "Enter a valid email.",
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          disabled={true}
                          placeholder="Enter your email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="phone_num"
                  rules={{
                    required: "Phone number is required.",
                    validate: validatePhone,
                  }}
                  disabled={!isEditing}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <PhoneInputField
                        value={field.value || ""}
                        onChange={field.onChange}
                        error={errors.phone_num?.message}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender</FormLabel>
                      <Select
                        disabled={!isEditing}
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Gender" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={Gender.MALE}>Male</SelectItem>
                          <SelectItem value={Gender.FEMALE}>Female</SelectItem>
                          <SelectItem value={Gender.OTHER}>Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-between">
                  {!isEditing ? (
                    <Button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="w-full"
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting || isUpdating}
                    >
                      {isSubmitting || isUpdating ? "Saving..." : "Save"}
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserForm;
